package fr.frux.feature.map

import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.core.maps.Location
import fr.frux.core.maps.MapCameraState
import fr.frux.core.maps.MapPyramid
import fr.frux.core.maps.MapPyramidPoint
import fr.frux.core.maps.MarkerBadge
import fr.frux.shared.domain.model.ClusterPyramid
import fr.frux.shared.domain.model.HarvestWindow
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.SpeciesCount
import kotlin.time.Instant

// Plafond de la couronne cote core-maps, qui le garde interne.
private const val PYRAMID_SATELLITES = 8

/**
 * Satellites d'un amas de la pyramide. Deux especes au meme badge font une seule pastille, dont
 * l'effectif est la somme. Le tri est stable : a effectif egal, l'ordre du serveur tient.
 */
internal fun pyramidBadges(
    species: List<SpeciesCount>,
    badgeOf: (Long) -> MarkerBadge?,
): List<MarkerBadge> {
    val totals = LinkedHashMap<MarkerBadge, Int>()
    for (entry in species) {
        val badge = badgeOf(entry.speciesId) ?: continue
        totals[badge] = (totals[badge] ?: 0) + entry.count
    }
    return totals.entries
        .sortedByDescending { it.value }
        .take(PYRAMID_SATELLITES)
        .map { it.key }
}

/**
 * Traduit la pyramide du domaine pour `core-maps`. Un arbre isole y prend exactement le marqueur
 * du lot detaille, cf. [TreeMarkerFacts]. Aucune selection ici : la pyramide ne sert qu'aux vues
 * larges, ou rien n'est selectionne.
 *
 * `@Composable` parce que la couleur suit le theme et que les libelles passent par `stringResource`.
 */
@Composable
fun ClusterPyramid.toMapPyramid(
    speciesById: Map<Long, Species>,
    speciesHarvests: Map<Long, List<HarvestWindow>>,
    currentMonth: Int,
    now: Instant,
): MapPyramid {
    val colors = MaterialTheme.fruxColors
    // Badge d'espece comme celui d'un arbre, sans partie : un amas melange les saisons.
    val badgeCache = HashMap<Long, MarkerBadge?>()
    val badgeOf = { id: Long ->
        badgeCache.getOrPut(id) {
            speciesById[id]?.let { species ->
                speciesGlyphKey(species.scientificName, null)?.let { glyph ->
                    MarkerBadge(iconKey = glyph, tint = speciesFamilyColor(species.scientificName, null, colors))
                }
            }
        }
    }
    val mapped =
        points.map { point ->
            val isolated = point.tree
            val facts = isolated?.let { point.markerFacts(speciesById[it.speciesId], currentMonth) }
            if (isolated != null && facts != null) {
                MapPyramidPoint.Single(
                    zoom = point.zoom,
                    marker =
                        facts.toMapMarker(
                            colors = colors,
                            harvests = speciesHarvests[isolated.speciesId].orEmpty(),
                            currentMonth = currentMonth,
                            now = now,
                            label = facts.markerLabel(),
                            selected = false,
                        ),
                )
            } else {
                MapPyramidPoint.Cluster(
                    position = Location(point.latitude, point.longitude),
                    zoom = point.zoom,
                    count = point.count,
                    expansionZoom = point.expansionZoom,
                    satellites = pyramidBadges(point.species, badgeOf),
                )
            }
        }
    return MapPyramid(maxZoom = maxZoom, points = mapped)
}

/**
 * [toMapPyramid] ne se recalcule que si ses entrees changent : `@Composable`, elle ne peut pas
 * vivre dans un `remember`, et la pyramide tient jusqu'a ~22 000 points. Le calcul est donc confie a
 * [PyramidMapping], qui ne retourne rien et que Compose saute tant que ses parametres sont les
 * memes. Null sans pyramide, ou le temps du premier calcul.
 */
@Composable
internal fun rememberMapPyramid(
    pyramid: ClusterPyramid?,
    state: MapState,
): MapPyramid? {
    val mapped = remember { mutableStateOf<MapPyramid?>(null) }
    // Memorises sur le referentiel : `speciesHarvests` est un getter qui rend une nouvelle carte a
    // chaque lecture, ce qui suffirait a empecher le saut.
    val speciesById = remember(state.species) { state.species.associateBy { it.id } }
    val harvests = remember(state.species) { state.speciesHarvests }
    if (pyramid != null) {
        PyramidMapping(pyramid, speciesById, harvests, state.currentMonth, state.now) { mapped.value = it }
    }
    return mapped.value.takeIf { pyramid != null }
}

@Composable
private fun PyramidMapping(
    pyramid: ClusterPyramid,
    speciesById: Map<Long, Species>,
    speciesHarvests: Map<Long, List<HarvestWindow>>,
    currentMonth: Int,
    now: Instant,
    onMapped: (MapPyramid) -> Unit,
) {
    val result = pyramid.toMapPyramid(speciesById, speciesHarvests, currentMonth, now)
    SideEffect { onMapped(result) }
}

/** Ce que l'apercu [PYRAMID_PREVIEW] donne a la carte : la pyramide, et si elle remplace le lot. */
internal class PyramidPreview(
    val pyramid: MapPyramid?,
    val shown: Boolean,
)

/** Au-dessus du zoom 14 la pyramide n'a plus de niveau : l'apercu s'arrete bien en deca. */
private const val PYRAMID_PREVIEW_MAX_ZOOM = 12f

/** Sorti de `MapSurface` (detekt `LongMethod`). Le seuil de zoom est lu par `derivedStateOf`. */
@Composable
internal fun rememberPyramidPreview(
    viewModel: MapViewModel,
    camera: MapCameraState,
    state: MapState,
): PyramidPreview {
    val clusterPyramid by viewModel.clusterPyramid.collectAsState()
    val pyramid = if (PYRAMID_PREVIEW) rememberMapPyramid(clusterPyramid, state) else null
    val shown by remember(camera) {
        derivedStateOf { PYRAMID_PREVIEW && camera.position.zoom < PYRAMID_PREVIEW_MAX_ZOOM }
    }
    return PyramidPreview(pyramid, shown)
}
